import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import { filter, keyBy } from 'lodash';
import { useEffect, useMemo } from 'react';

import { JobTagChip } from './job-tag-chip';
import { JobTagIcon } from './job-tag-icon';
import { useJobTags } from './use-job-tags';

interface JobTagSelectProps {
  /** Currently selected tag ids. */
  value: string[];
  onChange: (tagIds: string[]) => void;
  /** The job's species — narrows the offered tags. */
  species: string | null | undefined;
  label?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
}

/**
 * Multi-select over the vendor's enabled job tags, narrowed to the job's
 * species. Used inline in the booking flows, for a job that doesn't exist
 * yet; an existing job's tags are toggled one at a time through
 * `JobTagAddMenu`.
 *
 * The selection is kept in step with the species: a tag that stops applying
 * when the species changes is dropped rather than carried into a booking the
 * server would refuse.
 * @param root0
 * @param root0.value
 * @param root0.onChange
 * @param root0.species
 * @param root0.label
 * @param root0.disabled
 * @param root0.size
 */
export function JobTagSelect({
  value,
  onChange,
  species,
  label = 'Tags',
  disabled = false,
  size = 'small',
}: JobTagSelectProps) {
  const { tagsForSpecies, loaded } = useJobTags();

  const options = useMemo(
    () => tagsForSpecies(species),
    [tagsForSpecies, species],
  );

  const byId = useMemo(() => keyBy(options, '_id'), [options]);

  // Reconcile the selection with the species. An elk-only "Euro Mount" picked
  // before the booking was switched to beef would otherwise ride along —
  // shown as a nameless chip, since it is no longer among the options — into
  // a create the server rejects. Waits for the tags to load: pruning against
  // a still-empty list would wipe a selection being restored.
  useEffect(() => {
    if (!loaded) return;
    const applicable = filter(value, (id) => !!byId[id]);
    if (applicable.length !== value.length) {
      onChange(applicable);
    }
  }, [loaded, value, byId, onChange]);

  return (
    <FormControl size={size} fullWidth disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value as string[])}
        renderValue={(picked) =>
          (picked as string[]).map((id) => {
            const tag = byId[id];
            return (
              <JobTagChip
                key={id}
                tag={{ name: tag?.name ?? 'Tag', icon: tag?.icon ?? '' }}
                sx={{ mr: 0.5 }}
              />
            );
          })
        }
      >
        {options.length === 0 && (
          <MenuItem disabled value="">
            No tags available for this species
          </MenuItem>
        )}
        {options.map((tag) => (
          <MenuItem key={tag._id} value={tag._id}>
            <Checkbox size="small" checked={value.includes(tag._id)} />
            <ListItemIcon sx={{ minWidth: 32 }}>
              <JobTagIcon iconKey={tag.icon} fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={tag.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
